import React from "react"
import { Image } from "@chakra-ui/image"
import { Flex, Text, VStack } from "@chakra-ui/layout"

import { PIMSCopy } from "../resources/types"
import ResponsiveBlock from "~components/shared/layout/responsive-block"
import SectionHeading from "~components/shared/text/heading"
import Paragraph from "~components/shared/text/paragraph"

interface Props {
    copy: PIMSCopy
}

export default function BenefitsExplanationSection({ copy }: Props) {
    const { pims, benefits, benefitsTitle } = copy

    if (!benefits?.length) return null

    return (
        <ResponsiveBlock pt={{ base: 4, md: 16 }} pb={{ base: 16, md: 36 }}>
            <Flex align="center" justify="center" flexDir="column">
                <VStack spacing={{ base: 4, md: 6 }} mb={12}>
                    <SectionHeading
                        fontWeight="black"
                        size={{ base: "xl", md: "3xl" }}
                        maxW="900px"
                        textAlign={"center"}
                    >
                        {benefitsTitle || `How We Turbocharge ${pims}`}
                    </SectionHeading>
                </VStack>
                <Flex
                    pt={{ base: 4, md: 8 }}
                    gridGap={{ base: 8, md: 32 }}
                    flexDir={"column"}
                >
                    {benefits.map((benefit, index) => (
                        <BenefitSection
                            key={index}
                            twoWordTitle={benefit.twoWordTitle}
                            heading={benefit.heading}
                            paragraphs={benefit.paragraph}
                            image={benefit.image}
                            reversed={index % 2 === 1}
                        />
                    ))}
                </Flex>
            </Flex>
        </ResponsiveBlock>
    )
}

interface BenefitSectionProps {
    twoWordTitle: string
    heading: string
    paragraphs: string[]
    image: any
    color?: string
    reversed?: boolean
}

export function BenefitSection({
    twoWordTitle,
    heading,
    paragraphs,
    image,
    color = "brand",
    reversed,
}: BenefitSectionProps) {
    return (
        <Flex
            flexDir={{ base: "column", md: reversed ? "row-reverse" : "row" }}
            gridGap={[8, 12, 24, 28]}
            align="center"
        >
            <Flex flex={3}>
                <Image 
                    src={image} 
                    w="100%" 
                    alt={`${heading} - Chckvet feature illustration`}
                />
            </Flex>
            <Flex flex={3} minW="320px">
                <Flex flexDir="column">
                    <Text
                        as="h5"
                        fontSize="sm"
                        color={`${color}.500`}
                        fontWeight="black"
                        textTransform="uppercase"
                    >
                        {twoWordTitle}
                    </Text>
                    <SectionHeading
                        as="h6"
                        mt={"16px"}
                        lineHeight="tallest"
                        textAlign="left"
                        fontSize={{ base: "4xl", md: "4xl" }}
                    >
                        {heading}
                    </SectionHeading>
                    {paragraphs.map(p => (
                        <Paragraph key={p} my={"16px"} fontSize="lg">
                            {p}
                        </Paragraph>
                    ))}
                </Flex>
            </Flex>
        </Flex>
    )
}
